"use client";
import { useEscapeKeydown } from "@/hooks/useEscapeKeyDown";
import { usePrefersReducedMotion } from "@/hooks/usePrefersReducedMotion";
import useScrollLock from "@/hooks/useScrollLock";
import React, { useEffect, useLayoutEffect, useRef, useState } from "react";

export type ModalState = "open" | "closed";
/** "none" opens and closes instantly (like prefers-reduced-motion) */
export type ModalAnimation = "fade" | "staggered" | "none";

interface ModalProps {
  children?: React.ReactNode;
  open?: boolean;
  setOpen?: (open: boolean) => void;
  absolutePosition?: boolean;
  onMountedChange?: (mounted: boolean) => void;
  animation?: ModalAnimation;
  contentReady?: boolean;
}

type ModalContextProps = {
  open: boolean;
  setOpen: (open: boolean) => void;
  absolutePosition: boolean;
  /** true while open and during the exit animation */
  mounted: boolean;
  state: ModalState;
  animation: ModalAnimation;
  contentReady: boolean;
  onExitComplete: () => void;
};

const ModalContext = React.createContext<ModalContextProps | null>(null);

export function useModal() {
  const context = React.useContext(ModalContext);

  if (!context) {
    throw new Error("useModal must be used within <ModalRoot />");
  }

  return context;
}

/**
 * Keeps the modal mounted after closing until the exit animation is done.
 */
const usePresence = (open: boolean, animation: ModalAnimation) => {
  const [isExiting, setIsExiting] = useState(false);
  const [prevOpen, setPrevOpen] = useState(open);
  const prefersReducedMotion = usePrefersReducedMotion();
  // no exit animation to wait for
  const skipExit = prefersReducedMotion || animation === "none";

  // Adjust state during render (not in an effect) so there is never a committed
  // frame where the modal is closed but not yet exiting — that would unmount and
  // re-mount the modal, causing a flicker.
  if (open !== prevOpen || (isExiting && skipExit)) {
    setPrevOpen(open);
    setIsExiting(!open && !skipExit);
  }

  return {
    mounted: open || (isExiting && !skipExit),
    onExitComplete: () => {
      if (!open) setIsExiting(false);
    },
  };
};

const ModalRoot = ({
  children,
  open: openProps,
  setOpen: setOpenProps,
  absolutePosition = false,
  onMountedChange,
  animation = "fade",
  contentReady = true,
}: ModalProps) => {
  const [internalOpen, setInternalOpen] = useState(false);
  const open = openProps ?? internalOpen;
  const setOpen = setOpenProps ?? setInternalOpen;
  const lastOpenContentReady = useRef(contentReady);

  useLayoutEffect(() => {
    if (open) lastOpenContentReady.current = contentReady;
  }, [open, contentReady]);

  const { mounted, onExitComplete } = usePresence(open, animation);
  const previousMounted = useRef(mounted);

  useEffect(() => {
    // Notify only on transitions, including when Strict Mode replays effects.
    if (previousMounted.current === mounted) return;
    previousMounted.current = mounted;
    onMountedChange?.(mounted);
  }, [mounted, onMountedChange]);

  useEscapeKeydown((event) => {
    if (!open || event.defaultPrevented) return;
    event.preventDefault();
    setOpen(false);
  });

  // absolutePosition = modal lives inside a container (e.g. a map), so the page stays scrollable.
  // Unlock only after the exit animation, so the scrollbar doesn't reappear mid-fade.
  useScrollLock(mounted && !absolutePosition);

  return (
    <ModalContext.Provider
      value={{
        open,
        setOpen,
        absolutePosition,
        mounted,
        state: open ? "open" : "closed",
        animation,
        // A late load must not restart animations after closing has begun.
        contentReady: open ? contentReady : lastOpenContentReady.current,
        onExitComplete,
      }}
    >
      {children}
    </ModalContext.Provider>
  );
};

export default ModalRoot;
